import { message } from 'ant-design-vue'
import { ref, toRaw, type Ref } from 'vue'
import { handleFormError } from './useForm'

/**
 * 根据传入 record 初始化表单模型
 */
type InitModel<TModel, TRecord> = (record: TRecord) => TModel

/**
 * useModalForm 配置项
 */
type UseModalFormOptions<TModel extends Record<string, any>, TRecord> = {
  /** 初始化表单数据的方法（打开弹窗时调用） */
  initModel: InitModel<TModel, TRecord>
  /** 提交方法（接收当前表单模型，返回 Promise） */
  submitFn: (model: TModel | any) => Promise<unknown>
  /** 成功提示文案（可选） */
  successMsg?: string
  /** 失败提示文案（可选） */
  errorMsg?: string
  /** 提交成功后的回调（可选） */
  onSuccess?: (res: unknown) => void
}

export const useModalForm = <TModel extends Record<string, any>, TRecord = Partial<TModel>>(options: UseModalFormOptions<TModel, TRecord>) => {
  const visible = ref(false)
  const formRef = ref()
  const submitLoading = ref(false)
  const model = ref<TModel>({} as TModel) as Ref<TModel>

  const close = () => {
    visible.value = false
    formRef.value?.resetFields?.()
  }

  /**
   * 打开弹窗并根据 record 初始化表单
   */
  const open = (record?: TRecord) => {
    model.value = options.initModel((record ?? ({} as TRecord)) as TRecord)
    visible.value = true
  }

  /**
   * 提交表单：
   */
  const submit = async () => {
    if (submitLoading.value) return

    submitLoading.value = true
    try {
      await formRef.value?.validate?.()
      const res = await options.submitFn(toRaw(model.value) as TModel)
      message.success(options.successMsg ?? '操作成功')
      close()
      options.onSuccess?.(res)
    } catch (err) {
      console.log(err)
      handleFormError(err)
    } finally {
      submitLoading.value = false
    }
  }

  return {
    formRef,
    visible,
    submitLoading,
    model,
    open,
    close,
    submit
  }
}
